import React, { useEffect, useRef, useState } from "react";
import useInView from "../../hooks/useInView";

const easeOutQuad = (t) => t * (2 - t);
const NUMBER_PATTERN = /\d[\d,]*\.?\d*/g;

const formatValue = (value, { decimals, hasComma }) => {
  const fixed = value.toFixed(decimals);
  if (!hasComma) return fixed;
  const [intPart, decPart] = fixed.split(".");
  const withGrouping = Number(intPart).toLocaleString("en-US");
  return decPart ? `${withGrouping}.${decPart}` : withGrouping;
};

// "2,025 EA", "125.8%" 같은 문자열에서 숫자만 뽑아 0에서 목표값까지 카운트업
const CountUp = ({ text, duration = 700, className = "" }) => {
  const [ref, inView] = useInView({ threshold: 0, rootMargin: "0px 0px 0px 0px" });
  const [display, setDisplay] = useState(() => text.replace(NUMBER_PATTERN, "0"));
  const started = useRef(false);

  useEffect(() => {
    if (!inView || started.current) return;
    started.current = true;

    const tokens = text.match(NUMBER_PATTERN) || [];
    if (tokens.length === 0) {
      setDisplay(text);
      return;
    }

    const targets = tokens.map((token) => ({
      value: parseFloat(token.replace(/,/g, "")),
      decimals: token.includes(".") ? token.split(".")[1].length : 0,
      hasComma: token.includes(","),
    }));

    let raf;
    const start = performance.now();

    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = easeOutQuad(progress);
      let i = 0;
      const next = text.replace(NUMBER_PATTERN, () => {
        const target = targets[i];
        i += 1;
        return formatValue(target.value * eased, target);
      });
      setDisplay(next);

      if (progress < 1) {
        raf = requestAnimationFrame(tick);
      }
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, text, duration]);

  return (
    <span ref={ref} className={className}>
      {display}
    </span>
  );
};

export default CountUp;
